export const computeVelocityTrackFromFrames = (
    positionsFlat: number[],
    frames: number[],
    fps: number,
    smoothingSamples = 3
): number[] => {
    const n = Math.min(Math.floor(positionsFlat.length / 3), frames.length);
    if (n <= 0) return [];

    const safeFps = (typeof fps === 'number' && Number.isFinite(fps) && fps > 0) ? fps : 30;

    const speed = new Array<number>(n).fill(0);

    const getPos = (i: number) => {
        const o = i * 3;
        return {
            x: positionsFlat[o + 0] ?? 0,
            y: positionsFlat[o + 1] ?? 0,
            z: positionsFlat[o + 2] ?? 0
        };
    };

    const dist = (a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }) => {
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dz = a.z - b.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    };

    const dtSec = (aFrame: number, bFrame: number) => {
        const df = bFrame - aFrame;
        if (!Number.isFinite(df) || df === 0) return 0;
        return df / safeFps;
    };

    if (n === 1) {
        speed[0] = 0;
    } else {
        for (let i = 0; i < n; i++) {
            if (i === 0) {
                const p0 = getPos(0);
                const p1 = getPos(1);
                const dt = dtSec(frames[0], frames[1]);
                speed[i] = dt > 0 ? dist(p1, p0) / dt : 0;
            } else if (i === n - 1) {
                const p0 = getPos(n - 2);
                const p1 = getPos(n - 1);
                const dt = dtSec(frames[n - 2], frames[n - 1]);
                speed[i] = dt > 0 ? dist(p1, p0) / dt : 0;
            } else {
                const pPrev = getPos(i - 1);
                const pNext = getPos(i + 1);
                const dt = dtSec(frames[i - 1], frames[i + 1]);
                speed[i] = dt > 0 ? dist(pNext, pPrev) / dt : 0;
            }
        }
    }

    const samples = Math.max(1, Math.floor(smoothingSamples));
    if (samples <= 1) return speed;

    const radius = Math.floor(samples / 2);
    const smoothed = new Array<number>(n).fill(0);

    for (let i = 0; i < n; i++) {
        let sum = 0;
        let count = 0;
        const a = Math.max(0, i - radius);
        const b = Math.min(n - 1, i + radius);
        for (let j = a; j <= b; j++) {
            const v = speed[j];
            if (Number.isFinite(v)) {
                sum += v;
                count++;
            }
        }
        smoothed[i] = count > 0 ? (sum / count) : 0;
    }

    return smoothed;
};

export const getSpeedAtFrame = (
    positionsFlat: number[],
    frames: number[],
    speedsMps: number[],
    frame: number
): number => {
    const n = Math.min(Math.floor(positionsFlat.length / 3), frames.length, speedsMps.length);
    if (n <= 0) return 0;

    const f = (typeof frame === 'number' && Number.isFinite(frame)) ? frame : frames[0];

    if (n === 1) return speedsMps[0] ?? 0;

    // Clamp outside range.
    if (f <= frames[0]) return speedsMps[0] ?? 0;
    if (f >= frames[n - 1]) return speedsMps[n - 1] ?? 0;

    // Find the segment [i, i+1] containing f.
    let lo = 0;
    let hi = n - 2;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const a = frames[mid];
        const b = frames[mid + 1];
        if (f < a) {
            hi = mid - 1;
        } else if (f > b) {
            lo = mid + 1;
        } else {
            lo = mid;
            break;
        }
    }

    const i = Math.max(0, Math.min(n - 2, lo));
    const aF = frames[i];
    const bF = frames[i + 1];
    const aS = speedsMps[i] ?? 0;
    const bS = speedsMps[i + 1] ?? 0;

    const denom = (bF - aF);
    if (!Number.isFinite(denom) || denom === 0) return aS;

    const t = (f - aF) / denom;
    return aS + (bS - aS) * t;
};
